// src/lib/firebase/client.ts
'use client';

import { initializeApp, getApps, getApp, type FirebaseApp } from 'firebase/app';
import {
  initializeFirestore,
  getFirestore,
  type Firestore,
  persistentLocalCache,
  persistentMultipleTabManager,
} from 'firebase/firestore';
import { getAuth as _getAuth, type Auth } from 'firebase/auth';
import { getStorage, type FirebaseStorage } from 'firebase/storage';

type WebConfig = {
  apiKey: string;
  authDomain: string;
  projectId: string;
  appId: string;
  storageBucket?: string;
  messagingSenderId?: string;
  databaseURL?: string;
};

function parseMaybeJSON<T>(s?: string): T | undefined {
  if (!s) return undefined;
  try {
    return JSON.parse(s) as T;
  } catch {
    return undefined;
  }
}

/**
 * Resolve Firebase Web config with this priority:
 * 1) NEXT_PUBLIC_FIREBASE_WEBAPP_CONFIG (JSON string)
 * 2) FIREBASE_WEBAPP_CONFIG (Hosting/Studio env, JSON string)
 * 3) Individual NEXT_PUBLIC_FIREBASE_* vars
 * 4) Public fallback (your project)
 */
function resolveConfig(): WebConfig {
  const fromNextBlob = parseMaybeJSON<WebConfig>(process.env.NEXT_PUBLIC_FIREBASE_WEBAPP_CONFIG);
  if (fromNextBlob?.apiKey && fromNextBlob?.projectId) return fromNextBlob;

  const fromHostingBlob = parseMaybeJSON<WebConfig>(process.env.FIREBASE_WEBAPP_CONFIG as string | undefined);
  if (fromHostingBlob?.apiKey && fromHostingBlob?.projectId) return fromHostingBlob;

  const fromEnv: WebConfig = {
    apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY ?? '',
    authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN ?? '',
    projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID ?? '',
    appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID ?? '',
    storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET ?? '',
    messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID ?? '',
    databaseURL: process.env.NEXT_PUBLIC_FIREBASE_DATABASE_URL ?? '',
  };
  if (fromEnv.apiKey && fromEnv.projectId && fromEnv.appId && fromEnv.authDomain) return fromEnv;

  // Final public fallback (safe to ship — these are client SDK values)
  return {
    apiKey: 'AIzaSyDEkVBPSElVPpbCHZRwcaThVJMfdsKsEz4',
    authDomain: 'docplanner-grc.firebaseapp.com',
    projectId: 'docplanner-grc',
    appId: '1:945715792697:web:819bbef608302d4e25f6e8',
    storageBucket: 'docplanner-grc.firebasestorage.app',
    messagingSenderId: '945715792697',
    databaseURL: '',
  };
}

// Singletons across hot reloads
let _app: FirebaseApp | null = null;
let _db: Firestore | null = null;
let _auth: Auth | null = null;
let _storage: FirebaseStorage | null = null;

export function getFirebaseApp(): FirebaseApp {
  if (_app) return _app;

  const cfg = resolveConfig();

  if (typeof window !== 'undefined') {
    // eslint-disable-next-line no-console
    console.log('[Firebase] projectId =', cfg.projectId);
    // eslint-disable-next-line no-console
    console.log('[Firebase] authDomain =', cfg.authDomain);
    // eslint-disable-next-line no-console
    console.log('[Firebase] apiKey defined =', Boolean(cfg.apiKey));
  }

  _app = getApps().length ? getApp() : initializeApp(cfg);
  return _app;
}

export function getDb(): Firestore {
  if (_db) return _db;
  const app = getFirebaseApp();

  // Proxy/CDN friendly settings + offline cache with multi-tab coordination.
  try {
    _db = initializeFirestore(app, {
      experimentalAutoDetectLongPolling: true,
      localCache: persistentLocalCache({
        tabManager: persistentMultipleTabManager(),
      }),
    });
  } catch {
    // Fallback in case initializeFirestore throws (should be rare)
    _db = getFirestore(app);
  }

  return _db!;
}

export function getAuth(): Auth {
  if (_auth) return _auth;
  _auth = _getAuth(getFirebaseApp());
  return _auth!;
}

export function getStorageBucket(): FirebaseStorage {
  if (_storage) return _storage;
  _storage = getStorage(getFirebaseApp());
  return _storage!;
}